import React from 'react';
import { Quote, CheckCircle, ArrowRight, Star, Lightbulb, Target, Beaker, Leaf, Sparkles } from 'lucide-react';
import { Template } from './TemplateSelector';
import { slideTemplates } from './SlideTemplates';

export interface Slide {
  id: string;
  title: string;
  content: string;
  type: 'title' | 'content' | 'image' | 'list' | 'conclusion' | 'quote' | 'comparison' | 'image-text';
  notes?: string;
  imageUrl?: string;
  imageAlt?: string;
  template?: Template;
}

interface SlideRendererProps {
  slide: Slide;
  template: Template;
  isEditing?: boolean;
  onUpdate?: (updates: Partial<Slide>) => void;
}

const SlideRenderer: React.FC<SlideRendererProps> = ({
  slide,
  template,
  isEditing = false,
  onUpdate
}) => {
  // Enhanced content normalization function with better validation
  const normalizeContent = (content: any): string => {
    if (content === null || content === undefined) {
      return '';
    }

    if (typeof content === 'string') {
      // Clean the string content
      let cleanContent = content.trim();

      // Remove any JSON-like patterns
      if (cleanContent.includes('{"') ||
      cleanContent.includes('"title"') ||
      cleanContent.includes('"content"') ||
      cleanContent.includes('"imageKeywords"') ||
      cleanContent.startsWith('[{') ||
      cleanContent.startsWith('```json')) {
        console.warn('Filtering out JSON-like content from display:', cleanContent.substring(0, 100));
        return '';
      }

      return cleanContent;
    }

    if (Array.isArray(content)) {
      // If it's an array, join the elements
      return content.
      map((item) => normalizeContent(item)).
      filter((item) => item.trim()).
      join('\n');
    }

    if (typeof content === 'object') {
      try {
        // Check if this looks like malformed JSON that should be hidden
        const contentStr = JSON.stringify(content);
        if (contentStr.includes('"title"') ||
        contentStr.includes('"content"') ||
        contentStr.includes('"type"') ||
        contentStr.includes('"imageKeywords"')) {
          // This appears to be slide metadata, not display content
          console.warn('Detected malformed slide content containing metadata:', content);
          return '';
        }

        // If it's an object, try to extract meaningful text
        if (content.text) return normalizeContent(content.text);
        if (content.content) return normalizeContent(content.content);
        if (content.value) return normalizeContent(content.value);

        // Try to join object values, but exclude common metadata fields
        const filteredValues = Object.entries(content).
        filter(([key]) => !['id', 'type', 'template', 'imageUrl', 'imageAlt', 'imageKeywords'].includes(key)).
        map(([, value]) => normalizeContent(value)).
        filter((item) => item.trim());

        return filteredValues.join('\n');
      } catch (error) {
        console.error('Error processing object content:', error);
        return '';
      }
    }

    // Convert other types to string
    return String(content);
  };

  // Safely render HTML content with proper type checking and validation
  const renderHTMLContent = (content: any): string => {
    const normalizedContent = normalizeContent(content);

    if (!normalizedContent) {
      return '';
    }

    try {
      // Check if content contains HTML tags
      const hasHTMLTags = /<[^>]*>/g.test(normalizedContent);

      if (hasHTMLTags) {
        // Clean HTML content and convert to plain text
        const cleanContent = normalizedContent.
        replace(/<br\s*\/?>/gi, '\n').
        replace(/<\/center>/gi, '').
        replace(/<center>/gi, '').
        replace(/<h[1-6][^>]*>/gi, '').
        replace(/<\/h[1-6]>/gi, '').
        replace(/<p[^>]*>/gi, '').
        replace(/<\/p>/gi, '\n').
        replace(/<li[^>]*>/gi, '• ').
        replace(/<\/li>/gi, '\n').
        replace(/<[^>]*>/g, '').
        replace(/&nbsp;/g, ' ').
        replace(/&amp;/g, '&').
        replace(/&lt;/g, '<').
        replace(/&gt;/g, '>').
        replace(/&quot;/g, '"').
        trim();

        return cleanContent;
      }

      return normalizedContent;
    } catch (error) {
      console.error('Error processing HTML content:', error, 'Content:', content);
      return normalizedContent || '';
    }
  };

  // Safe split function with comprehensive type checking
  const safeTextSplit = (text: any, delimiter: string = '\n'): string[] => {
    const normalizedText = normalizeContent(text);

    if (!normalizedText) {
      return [''];
    }

    try {
      // Split the text and filter out empty lines
      return normalizedText.
      split(delimiter).
      map((line) => line.trim()).
      filter((line) => line.length > 0);
    } catch (error) {
      console.error('Error splitting text:', error, 'Text:', text);
      return [normalizedText];
    }
  };

  // Enhanced content processing for lists
  const processListContent = (content: any): string[] => {
    const normalizedContent = normalizeContent(content);

    if (!normalizedContent) return [];

    // If content is already an array format, handle it
    if (Array.isArray(content)) {
      return content.
      map((item) => normalizeContent(item)).
      filter((item) => item.trim()).
      map((item) => item.replace(/^[•\-\*]\s*/, ''));
    }

    // Split by various delimiters and clean up
    const lines = normalizedContent.
    split(/[\n\r•]/).
    map((line) => line.trim()).
    filter((line) => line.length > 0).
    map((line) => line.replace(/^[\-\*•]\s*/, ''));

    return lines.length > 0 ? lines : [normalizedContent];
  };

  const renderSlideByTemplate = () => {
    const { preview, slides: templateConfig } = template;
    const isTitle = slide.type === 'title';
    const config = isTitle ? preview.titleSlide : preview.contentSlide;

    // Enhanced slide rendering based on template and type
    switch (slide.type) {
      case 'title':
        return renderTitleSlide(config, preview.fonts);
      case 'quote':
        return renderQuoteSlide(config, preview.fonts);
      case 'image':
      case 'image-text':
        return renderImageSlide(config, preview.fonts);
      case 'comparison':
        return renderComparisonSlide(config, preview.fonts);
      case 'list':
        return renderListSlide(config, preview.fonts);
      case 'conclusion':
        return renderConclusionSlide(config, preview.fonts);
      default:
        return renderContentSlide(config, preview.fonts);
    }
  };

  const renderTitleSlide = (config: any, fonts: any) =>
  <div
    className="h-full flex flex-col justify-center items-center text-center relative overflow-hidden"
    style={{ background: config.backgroundColor }} data-id="73qytfjh1" data-path="src/components/ai/SlideRenderer.tsx">

      {/* Enhanced decorative background elements based on template */}
      {renderBackgroundDecorations(template.id)}
      
      {slide.imageUrl &&
    <div className="absolute inset-0 z-0" data-id="5tjqli4xg" data-path="src/components/ai/SlideRenderer.tsx">
          <img
        src={slide.imageUrl}
        alt={slide.imageAlt || slide.title}
        className="w-full h-full object-cover opacity-20"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }} data-id="bk5dcrs2d" data-path="src/components/ai/SlideRenderer.tsx" />
          <div className="absolute inset-0 bg-black/30" data-id="k7iwu357a" data-path="src/components/ai/SlideRenderer.tsx"></div>
        </div>
    }
      
      <div className="relative z-10 p-8 max-w-4xl" data-id="ofkn6nyk1" data-path="src/components/ai/SlideRenderer.tsx">
        {isEditing ?
      <input
        value={slide.title || ''}
        onChange={(e) => onUpdate?.({ title: e.target.value })}
        className="text-4xl md:text-6xl font-bold mb-6 bg-transparent border-none outline-none text-center w-full"
        style={{
          color: config.textColor,
          fontFamily: fonts.heading
        }} data-id="l93mie7bu" data-path="src/components/ai/SlideRenderer.tsx" /> :
      <h1
        className="text-4xl md:text-6xl font-bold mb-6 drop-shadow-lg"
        style={{
          color: config.textColor,
          fontFamily: fonts.heading
        }} data-id="r979g30i2" data-path="src/components/ai/SlideRenderer.tsx">
            {renderHTMLContent(slide.title)}
          </h1>
      }
        
        {isEditing ?
      <textarea
        value={slide.content || ''}
        onChange={(e) => onUpdate?.({ content: e.target.value })}
        className="text-xl md:text-2xl leading-relaxed bg-transparent border-none outline-none text-center w-full resize-none"
        style={{
          color: config.textColor,
          fontFamily: fonts.body,
          opacity: 0.9
        }}
        rows={3} data-id="ysfqndhqt" data-path="src/components/ai/SlideRenderer.tsx" /> :
      <div
        className="text-xl md:text-2xl leading-relaxed drop-shadow"
        style={{
          color: config.textColor,
          fontFamily: fonts.body,
          opacity: 0.9
        }} data-id="mtqf2g5q7" data-path="src/components/ai/SlideRenderer.tsx">
            {safeTextSplit(renderHTMLContent(slide.content)).map((line, i) =>
        <div key={i} data-id="pkve8nqlz" data-path="src/components/ai/SlideRenderer.tsx">{line}</div>
        )}
          </div>
      }
        
        {/* Enhanced accent elements */}
        <div
        className="w-24 h-1 mx-auto mt-6 rounded-full"
        style={{ backgroundColor: config.accentColor }} data-id="o2v9vgml9" data-path="src/components/ai/SlideRenderer.tsx">
        </div>
        
        {/* Template-specific decorative icons */}
        {renderTemplateIcon(template.id, config.accentColor)}
      </div>
    </div>;

  const renderQuoteSlide = (config: any, fonts: any) =>
  <div
    className="h-full flex flex-col justify-center items-center text-center p-12 relative"
    style={{ backgroundColor: config.backgroundColor }} data-id="gbsvag2kb" data-path="src/components/ai/SlideRenderer.tsx">

      {/* Large decorative quote mark */}
      <div
      className="absolute text-9xl opacity-5 select-none"
      style={{
        color: config.accentColor,
        top: '20%',
        left: '50%',
        transform: 'translateX(-50%)',
        fontFamily: fonts.heading
      }} data-id="tuty4d3lh" data-path="src/components/ai/SlideRenderer.tsx">
        "
      </div>
      
      <Quote
      className="h-16 w-16 mb-6 opacity-50"
      style={{ color: config.accentColor }} data-id="6850jg4e7" data-path="src/components/ai/SlideRenderer.tsx" />

      {isEditing ?
    <textarea
      value={slide.content || ''}
      onChange={(e) => onUpdate?.({ content: e.target.value })}
      className="text-2xl md:text-3xl font-medium italic mb-6 leading-relaxed bg-transparent border-none outline-none text-center w-full resize-none relative z-10"
      style={{
        color: config.textColor,
        fontFamily: fonts.heading
      }}
      rows={4} data-id="p0yb7fnlt" data-path="src/components/ai/SlideRenderer.tsx" /> :
    <blockquote
      className="text-2xl md:text-3xl font-medium italic mb-6 leading-relaxed max-w-4xl relative z-10"
      style={{
        color: config.textColor,
        fontFamily: fonts.heading
      }} data-id="04cfoakbr" data-path="src/components/ai/SlideRenderer.tsx">
          "{renderHTMLContent(slide.content)}"
        </blockquote>
    }
      
      {isEditing ?
    <input
      value={slide.title || ''}
      onChange={(e) => onUpdate?.({ title: e.target.value })}
      className="text-lg bg-transparent border-none outline-none text-center relative z-10"
      style={{
        color: config.textColor,
        fontFamily: fonts.body,
        opacity: 0.8
      }} data-id="zyumkxz0o" data-path="src/components/ai/SlideRenderer.tsx" /> :
    <cite
      className="text-lg relative z-10"
      style={{
        color: config.textColor,
        fontFamily: fonts.body,
        opacity: 0.8
      }} data-id="tkuqk8y28" data-path="src/components/ai/SlideRenderer.tsx">
          — {renderHTMLContent(slide.title)}
        </cite>
    }
    </div>;

  const renderImageSlide = (config: any, fonts: any) =>
  <div
    className="h-full flex flex-row"
    style={{ backgroundColor: config.backgroundColor }} data-id="bhpf0tmt4" data-path="src/components/ai/SlideRenderer.tsx">

      {slide.imageUrl &&
    <div className="w-1/2 relative" data-id="omr2fba1y" data-path="src/components/ai/SlideRenderer.tsx">
          <img
        src={slide.imageUrl}
        alt={slide.imageAlt || slide.title}
        className="w-full h-full object-cover"
        onError={(e) => {
          e.currentTarget.src = `https://source.unsplash.com/600x400/?${encodeURIComponent(slide.title || 'education')}`;
        }} data-id="9k3ebjdmc" data-path="src/components/ai/SlideRenderer.tsx" />
          <div className="absolute inset-0 bg-black/10" data-id="w7rovi9a8" data-path="src/components/ai/SlideRenderer.tsx"></div>
          
          {/* Image overlay decoration based on template */}
          {template.id === 'creative-purple' &&
      <div className="absolute top-4 right-4 w-12 h-12 bg-white/20 rounded-full blur-sm" data-id="83s8u189m" data-path="src/components/ai/SlideRenderer.tsx"></div>
      }
        </div>
    }
      
      <div className="w-1/2 p-8 flex flex-col justify-center" data-id="juqj0jecn" data-path="src/components/ai/SlideRenderer.tsx">
        {isEditing ?
      <input
        value={slide.title || ''}
        onChange={(e) => onUpdate?.({ title: e.target.value })}
        className="text-2xl font-bold mb-4 bg-transparent border-none outline-none"
        style={{
          color: config.textColor,
          fontFamily: fonts.heading
        }} data-id="vf8c1cows" data-path="src/components/ai/SlideRenderer.tsx" /> :
      <h2
        className="text-2xl font-bold mb-4"
        style={{
          color: config.textColor,
          fontFamily: fonts.heading
        }} data-id="chu6ckzj4" data-path="src/components/ai/SlideRenderer.tsx">
            {renderHTMLContent(slide.title)}
          </h2>
      }
        
        {isEditing ?
      <textarea
        value={slide.content || ''}
        onChange={(e) => onUpdate?.({ content: e.target.value })}
        className="leading-relaxed text-lg bg-transparent border-none outline-none resize-none flex-1"
        style={{
          color: config.textColor,
          fontFamily: fonts.body
        }} data-id="a3w92f2op" data-path="src/components/ai/SlideRenderer.tsx" /> :
      <div
        className="leading-relaxed text-lg space-y-4"
        style={{
          color: config.textColor,
          fontFamily: fonts.body
        }} data-id="za8filyfr" data-path="src/components/ai/SlideRenderer.tsx">
            {safeTextSplit(renderHTMLContent(slide.content)).map((line, i) =>
        <p key={i} data-id="dsetlczb3" data-path="src/components/ai/SlideRenderer.tsx">{line}</p>
        )}
          </div>
      }
      </div>
    </div>;

  const renderListSlide = (config: any, fonts: any) =>
  <div
    className="h-full p-8"
    style={{ backgroundColor: config.backgroundColor }} data-id="ykitftlle" data-path="src/components/ai/SlideRenderer.tsx">

      {isEditing ?
    <input
      value={slide.title || ''}
      onChange={(e) => onUpdate?.({ title: e.target.value })}
      className="text-2xl font-bold mb-6 bg-transparent border-none outline-none w-full"
      style={{
        color: config.textColor,
        fontFamily: fonts.heading
      }} data-id="x1xknlxbq" data-path="src/components/ai/SlideRenderer.tsx" /> :
    <h2
      className="text-2xl font-bold mb-6"
      style={{
        color: config.textColor,
        fontFamily: fonts.heading
      }} data-id="0x7ykuqvo" data-path="src/components/ai/SlideRenderer.tsx">
          {renderHTMLContent(slide.title)}
        </h2>
    }
      
      {isEditing ?
    <textarea
      value={slide.content || ''}
      onChange={(e) => onUpdate?.({ content: e.target.value })}
      className="w-full h-64 bg-transparent border-none outline-none resize-none"
      style={{
        color: config.textColor,
        fontFamily: fonts.body
      }}
      placeholder="Enter list items, one per line..." data-id="itga4rzrw" data-path="src/components/ai/SlideRenderer.tsx" /> :
    <ul className="space-y-6 max-w-4xl" data-id="c8vllxtag" data-path="src/components/ai/SlideRenderer.tsx">
          {processListContent(slide.content).map((item, i) =>
      <li key={i} className="flex items-start space-x-4" data-id="5sfi9kpfo" data-path="src/components/ai/SlideRenderer.tsx">
              {renderBulletPoint(template.id, config.accentColor, i)}
              <span
          className="text-lg leading-relaxed"
          style={{
            color: config.textColor,
            fontFamily: fonts.body
          }} data-id="9e1irulxc" data-path="src/components/ai/SlideRenderer.tsx">
                {item}
              </span>
            </li>
      )}
        </ul>
    }
    </div>;

  const renderComparisonSlide = (config: any, fonts: any) => {
    const parts = safeTextSplit(renderHTMLContent(slide.content), '\n\n');
    return (
      <div
        className="h-full p-8"
        style={{ backgroundColor: config.backgroundColor }} data-id="bjxd0m1xd" data-path="src/components/ai/SlideRenderer.tsx">

        {isEditing ?
        <input
          value={slide.title || ''}
          onChange={(e) => onUpdate?.({ title: e.target.value })}
          className="text-2xl font-bold text-center mb-8 bg-transparent border-none outline-none w-full"
          style={{
            color: config.textColor,
            fontFamily: fonts.heading
          }} data-id="4jugqiubn" data-path="src/components/ai/SlideRenderer.tsx" /> :
        <h2
          className="text-2xl font-bold text-center mb-8"
          style={{
            color: config.textColor,
            fontFamily: fonts.heading
          }} data-id="6882ff5if" data-path="src/components/ai/SlideRenderer.tsx">
            {renderHTMLContent(slide.title)}
          </h2>
        }
        
        {isEditing ?
        <textarea
          value={slide.content || ''}
          onChange={(e) => onUpdate?.({ content: e.target.value })}
          className="w-full h-64 bg-transparent border-none outline-none resize-none"
          style={{
            color: config.textColor,
            fontFamily: fonts.body
          }}
          placeholder="Separate comparison sections with double line breaks..." data-id="qv35yjvoq" data-path="src/components/ai/SlideRenderer.tsx" /> :
        <div className="grid grid-cols-3 gap-8 h-4/5" data-id="h14rm6i4q" data-path="src/components/ai/SlideRenderer.tsx">
            <div
            className="p-6 rounded-lg border-l-4 h-fit"
            style={{
              backgroundColor: `${config.accentColor}10`,
              borderLeftColor: config.accentColor
            }} data-id="8zaz6g5bk" data-path="src/components/ai/SlideRenderer.tsx">
              <div
              className="leading-relaxed"
              style={{
                color: config.textColor,
                fontFamily: fonts.body
              }} data-id="k3pgmbcua" data-path="src/components/ai/SlideRenderer.tsx">
                {safeTextSplit(parts[0] || '').map((line, i) =>
              <p key={i} className="mb-2" data-id="hwkoljvmy" data-path="src/components/ai/SlideRenderer.tsx">{line}</p>
              )}
              </div>
            </div>
            
            <div className="flex items-center justify-center" data-id="0nyh941x6" data-path="src/components/ai/SlideRenderer.tsx">
              <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-lg"
              style={{ backgroundColor: config.accentColor }} data-id="dg7ntdip1" data-path="src/components/ai/SlideRenderer.tsx">
                VS
              </div>
            </div>
            
            <div
            className="p-6 rounded-lg border-l-4 h-fit"
            style={{
              backgroundColor: `${config.accentColor}20`,
              borderLeftColor: config.accentColor
            }} data-id="s322ew70q" data-path="src/components/ai/SlideRenderer.tsx">
              <div
              className="leading-relaxed"
              style={{
                color: config.textColor,
                fontFamily: fonts.body
              }} data-id="ep3mzn1or" data-path="src/components/ai/SlideRenderer.tsx">
                {safeTextSplit(parts[1] || '').map((line, i) =>
              <p key={i} className="mb-2" data-id="fw2666ggd" data-path="src/components/ai/SlideRenderer.tsx">{line}</p>
              )}
              </div>
            </div>
          </div>
        }
      </div>);
  };

  const renderConclusionSlide = (config: any, fonts: any) =>
  <div
    className="h-full flex flex-col justify-center p-12 text-center relative"
    style={{ backgroundColor: config.backgroundColor }} data-id="og519mhha" data-path="src/components/ai/SlideRenderer.tsx">

      {/* Enhanced decorative elements */}
      {renderConclusionDecorations(template.id, config.accentColor)}
      
      <div className="relative z-10" data-id="irge01erj" data-path="src/components/ai/SlideRenderer.tsx">
        {isEditing ?
      <input
        value={slide.title || ''}
        onChange={(e) => onUpdate?.({ title: e.target.value })}
        className="text-3xl font-bold mb-6 bg-transparent border-none outline-none text-center"
        style={{
          color: config.textColor,
          fontFamily: fonts.heading
        }} data-id="k54toh3u2" data-path="src/components/ai/SlideRenderer.tsx" /> :
      <h2
        className="text-3xl font-bold mb-6"
        style={{
          color: config.textColor,
          fontFamily: fonts.heading
        }} data-id="z4uogkzx0" data-path="src/components/ai/SlideRenderer.tsx">
            {renderHTMLContent(slide.title)}
          </h2>
      }
        
        {isEditing ?
      <textarea
        value={slide.content || ''}
        onChange={(e) => onUpdate?.({ content: e.target.value })}
        className="text-xl leading-relaxed mb-8 bg-transparent border-none outline-none text-center resize-none"
        style={{
          color: config.textColor,
          fontFamily: fonts.body
        }}
        rows={4} data-id="b4jwcelvj" data-path="src/components/ai/SlideRenderer.tsx" /> :
      <div
        className="text-xl leading-relaxed mb-8 max-w-3xl mx-auto"
        style={{
          color: config.textColor,
          fontFamily: fonts.body
        }} data-id="vdpdlrg1g" data-path="src/components/ai/SlideRenderer.tsx">
            {safeTextSplit(renderHTMLContent(slide.content)).map((line, i) =>
        <p key={i} className="mb-4" data-id="k2r2ow419" data-path="src/components/ai/SlideRenderer.tsx">{line}</p>
        )}
          </div>
      }
        
        <div
        className="text-3xl font-bold"
        style={{ color: config.accentColor }} data-id="g6v00falh" data-path="src/components/ai/SlideRenderer.tsx">
          Thank You!
        </div>
        
        {/* Enhanced decorative line */}
        <div
        className="w-32 h-1 mx-auto mt-6 rounded-full shadow-sm"
        style={{ backgroundColor: config.accentColor }} data-id="5bvn8rytf" data-path="src/components/ai/SlideRenderer.tsx">
        </div>
      </div>
    </div>;

  const renderContentSlide = (config: any, fonts: any) =>
  <div
    className="h-full p-8"
    style={{ backgroundColor: config.backgroundColor }} data-id="vh2ly8xha" data-path="src/components/ai/SlideRenderer.tsx">

      {isEditing ?
    <input
      value={slide.title || ''}
      onChange={(e) => onUpdate?.({ title: e.target.value })}
      className="text-2xl font-bold mb-6 bg-transparent border-none outline-none w-full"
      style={{
        color: config.textColor,
        fontFamily: fonts.heading
      }} data-id="88wjccri7" data-path="src/components/ai/SlideRenderer.tsx" /> :
    <h2
      className="text-2xl font-bold mb-6"
      style={{
        color: config.textColor,
        fontFamily: fonts.heading
      }} data-id="2itwk6tgp" data-path="src/components/ai/SlideRenderer.tsx">
          {renderHTMLContent(slide.title)}
        </h2>
    }
      
      {isEditing ?
    <textarea
      value={slide.content || ''}
      onChange={(e) => onUpdate?.({ content: e.target.value })}
      className="w-full h-64 leading-relaxed text-lg bg-transparent border-none outline-none resize-none"
      style={{
        color: config.textColor,
        fontFamily: fonts.body
      }} data-id="6rh1b9p7j" data-path="src/components/ai/SlideRenderer.tsx" /> :
    <div
      className="leading-relaxed text-lg space-y-4"
      style={{
        color: config.textColor,
        fontFamily: fonts.body
      }} data-id="bo6la7dpp" data-path="src/components/ai/SlideRenderer.tsx">
          {safeTextSplit(renderHTMLContent(slide.content)).map((line, i) =>
      <p key={i} data-id="t5ipncky6" data-path="src/components/ai/SlideRenderer.tsx">{line}</p>
      )}
        </div>
    }
    </div>;

  // Helper functions for enhanced visual elements
  const renderBackgroundDecorations = (templateId: string) => {
    switch (templateId) {
      case 'modern-gradient':
        return (
          <div className="absolute inset-0" data-id="pvx1n1xjp" data-path="src/components/ai/SlideRenderer.tsx">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-sm" data-id="fuhewceun" data-path="src/components/ai/SlideRenderer.tsx"></div>
            <div className="absolute bottom-20 right-20 w-24 h-24 bg-white/10 rounded-full blur-sm" data-id="xdob5howt" data-path="src/components/ai/SlideRenderer.tsx"></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/5 rounded-full blur-sm" data-id="lebstbm58" data-path="src/components/ai/SlideRenderer.tsx"></div>
          </div>);

      case 'nature-green':
        return (
          <div className="absolute inset-0" data-id="1aqn7v62u" data-path="src/components/ai/SlideRenderer.tsx">
            <div className="absolute top-16 right-16 w-20 h-20 bg-white/10 rounded-full blur-sm" data-id="8crs6h05y" data-path="src/components/ai/SlideRenderer.tsx"></div>
            <div className="absolute bottom-16 left-16 w-28 h-28 bg-white/5 rounded-full blur-sm" data-id="vg3qewllp" data-path="src/components/ai/SlideRenderer.tsx"></div>
          </div>);

      case 'creative-purple':
        return (
          <div className="absolute inset-0" data-id="ixmk8a481" data-path="src/components/ai/SlideRenderer.tsx">
            <div className="absolute top-12 left-12 w-24 h-24 bg-white/15 rounded-full blur-md" data-id="cdoq2npl7" data-path="src/components/ai/SlideRenderer.tsx"></div>
            <div className="absolute bottom-12 right-12 w-32 h-32 bg-white/10 rounded-full blur-lg" data-id="4r8sifqnz" data-path="src/components/ai/SlideRenderer.tsx"></div>
            <div className="absolute top-1/3 right-1/3 w-16 h-16 bg-white/8 rounded-full blur-sm" data-id="gvipfhg6t" data-path="src/components/ai/SlideRenderer.tsx"></div>
          </div>);

      default:
        return null;
    }
  };

  const renderTemplateIcon = (templateId: string, accentColor: string) => {
    const iconProps = { className: "w-8 h-8 mt-4 opacity-60", style: { color: accentColor } };

    switch (templateId) {
      case 'science-lab':
        return <Beaker {...iconProps} data-id="vblurecvy" data-path="src/components/ai/SlideRenderer.tsx" />;
      case 'nature-green':
        return <Leaf {...iconProps} data-id="2nhhbc4at" data-path="src/components/ai/SlideRenderer.tsx" />;
      case 'creative-purple':
        return <Sparkles {...iconProps} data-id="5jj31j1m2" data-path="src/components/ai/SlideRenderer.tsx" />;
      default:
        return null;
    }
  };

  const renderBulletPoint = (templateId: string, accentColor: string, index: number) => {
    switch (templateId) {
      case 'education-playful':
        return (
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            style={{ backgroundColor: accentColor }} data-id="xqelp9hrc" data-path="src/components/ai/SlideRenderer.tsx">
            <span className="text-white text-sm font-bold" data-id="dglu2xbl0" data-path="src/components/ai/SlideRenderer.tsx">{index + 1}</span>
          </div>);

      case 'science-lab':
        return (
          <div
            className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-1"
            style={{ backgroundColor: accentColor }} data-id="4qsb5nuhv" data-path="src/components/ai/SlideRenderer.tsx">
            <div className="w-2 h-2 bg-white rounded-full" data-id="os9smcaaq" data-path="src/components/ai/SlideRenderer.tsx"></div>
          </div>);

      case 'nature-green':
        return (
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            style={{ backgroundColor: accentColor }} data-id="qlcalwfnz" data-path="src/components/ai/SlideRenderer.tsx">
            <Leaf className="w-3 h-3 text-white" data-id="31qi82hv6" data-path="src/components/ai/SlideRenderer.tsx" />
          </div>);

      default:
        return (
          <div
            className="w-3 h-3 rounded-full mt-2 flex-shrink-0"
            style={{ backgroundColor: accentColor }} data-id="w0jxz9jmy" data-path="src/components/ai/SlideRenderer.tsx" />);
    }
  };

  const renderConclusionDecorations = (templateId: string, accentColor: string) => {
    switch (templateId) {
      case 'education-playful':
        return (
          <div className="absolute inset-0" data-id="jmxb13vdx" data-path="src/components/ai/SlideRenderer.tsx">
            <div className="absolute top-8 left-1/2 transform -translate-x-1/2" data-id="ae4j3hk9h" data-path="src/components/ai/SlideRenderer.tsx">
              <Star className="h-12 w-12 opacity-20" style={{ color: accentColor }} data-id="ll3rsd04z" data-path="src/components/ai/SlideRenderer.tsx" />
            </div>
            <div className="absolute top-16 left-16 w-8 h-8 bg-yellow-300/20 rounded-full" data-id="yf50ba9fv" data-path="src/components/ai/SlideRenderer.tsx"></div>
            <div className="absolute top-20 right-20 w-6 h-6 bg-pink-300/20 rounded-full" data-id="1plzc0yw9" data-path="src/components/ai/SlideRenderer.tsx"></div>
          </div>);

      case 'corporate-blue':
        return (
          <div className="absolute top-8 left-1/2 transform -translate-x-1/2" data-id="5vn3ixlwj" data-path="src/components/ai/SlideRenderer.tsx">
            <Target className="h-12 w-12 opacity-20" style={{ color: accentColor }} data-id="aorpb7f0m" data-path="src/components/ai/SlideRenderer.tsx" />
          </div>);

      default:
        return (
          <div className="absolute top-8 left-1/2 transform -translate-x-1/2" data-id="uiiv3alnw" data-path="src/components/ai/SlideRenderer.tsx">
            <Star className="h-12 w-12 opacity-20" style={{ color: accentColor }} data-id="frt4rf3ol" data-path="src/components/ai/SlideRenderer.tsx" />
          </div>);
    }
  };

  return (
    <div className="h-full bg-white border rounded-lg shadow-lg overflow-hidden" data-id="k2j86fhsa" data-path="src/components/ai/SlideRenderer.tsx">
      {renderSlideByTemplate()}
    </div>);
};

export default SlideRenderer;